import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import B3Filter from '@/components/filter/B3Filter';
import B3Spin from '@/components/spin/B3Spin';
import { B2BAutoCompleteCheckbox } from '@/components/ui/B2BAutoCompleteCheckbox';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMobile } from '@/hooks/useMobile';
import { useB3Lang } from '@/lib/lang';
import { PageInfo } from '@/shared/service/bc/graphql/base';
import { fetchOrderComments, searchOrderComments } from '@/shared/service/api/orderComments';
import {
  CompanyOrdersFiltersInput,
  getCompanyOrders,
  getCustomerOrders,
  OrdersFiltersInput,
  OrdersSortInput,
} from '@/shared/service/bc/graphql/orders';
import { isB2BUserSelector, useAppSelector } from '@/store';
import { CustomerRole } from '@/types';
import { currencyFormat, ordersCurrencyFormat } from '@/utils/b3CurrencyFormat';
import { displayFormat } from '@/utils/b3DateFormat';

import OrderStatus from './components/OrderStatus';
import { B3Table, PossibleNodeWrapper, TableColumnItem } from './table/B3Table';
import {
  adaptCompanyUnifiedToLegacyFilterParams,
  adaptUnifiedToLegacyFilterParams,
} from './adaptUnifiedToLegacyFilterParams';
import {
  FilterSearchProps,
  getFilterMoreData,
  getOrderStatusText,
  translateFilterMoreData,
} from './config';
import { type ListItem, mapSfGqlOrderToListItem } from './mapSfGqlOrderToListItem';
import { OrderItemCard } from './OrderItemCard';
import {
  getB2BAllOrders,
  getBCAllOrders,
  getBcOrderStatusType,
  getCreatedByUserForOrders,
  getOrderStatusType,
} from './orders';
import { useCompanyOrdersState } from './useCompanyOrdersState';
import { useCustomerOrdersState } from './useCustomerOrdersState';
import { useLegacyOrdersFilterState } from './useLegacyOrdersFilterState';

interface OrderProps {
  isCompanyOrder?: boolean;
}

function useData() {
  const isB2BUser = useAppSelector(isB2BUserSelector);
  const companyB2BId = useAppSelector(({ company }) => company.companyInfo.id);
  const role = useAppSelector(({ company }) => company.customer.role);
  const salesRepCompanyId = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id);
  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);

  const { order: orderSubViewPermission } = useAppSelector(
    ({ company }) => company.pagesSubsidiariesPermission,
  );

  const { selectCompanyHierarchyId, isEnabledCompanyHierarchy } = useAppSelector(
    ({ company }) => company.companyHierarchyInfo,
  );

  const currentCompanyId =
    role === CustomerRole.SUPER_ADMIN && isAgenting
      ? Number(salesRepCompanyId)
      : Number(companyB2BId);

  const companyId = companyB2BId || salesRepCompanyId;

  return {
    role,
    isAgenting,
    isB2BUser,
    isEnabledCompanyHierarchy: isEnabledCompanyHierarchy && orderSubViewPermission,
    selectedCompanyId: Number(selectCompanyHierarchyId) || currentCompanyId,
    companyId,
  };
}

const getEmail = (haystack: string = '') => {
  const getEmailReg = /\((.+)\)/g;
  const emailRegArr = getEmailReg.exec(haystack);

  return emailRegArr?.length ? emailRegArr[1] : '';
};

const getName = (haystack: string = '') => {
  const getCreatedByReg = /^[^(]+/;
  const createdByUserRegArr = getCreatedByReg.exec(haystack);
  return createdByUserRegArr?.length ? createdByUserRegArr[0].trim() : '';
};

type LegacyOrderListItem = ListItem;

function Order({ isCompanyOrder = false }: OrderProps) {
  const b3Lang = useB3Lang();
  const [isMobile] = useMobile();
  const isUnifiedOrders = useFeatureFlag('B2B-4613.buyer_portal_unified_sf_gql_orders');
  const {
    role,
    isAgenting,
    companyId,
    isB2BUser,
    isEnabledCompanyHierarchy,
    selectedCompanyId,
  } = useData();

  const customerId = useAppSelector(({ company }) => company.customer.id);

  const [legacyPagination, setLegacyPagination] = useState({ offset: 0, first: 10 });

  const [allTotal, setAllTotal] = useState(0);
  const [filterMoreInfo, setFilterMoreInfo] = useState<Array<any>>([]);
  const [getOrderStatuses, setOrderStatuses] = useState<Array<any>>([]);
  const [orderComments, setOrderComments] = useState<
    Record<string, { internalOrderId: string; invoiceId: string; companyName: string }>
  >({});

  // MongoDB parallel search state: queried alongside BC whenever a search term is active.
  // null = not yet queried for the current term; [] = queried, no results; ListItem[] = results.
  const [mongoListItems, setMongoListItems] = useState<ListItem[] | null>(null);
  const [mongoTotalCount, setMongoTotalCount] = useState(0);

  const isUnifiedCustomerPath = isUnifiedOrders && !isCompanyOrder;
  const isUnifiedCompanyPath = isUnifiedOrders && isCompanyOrder;

  const legacyFilterState = useLegacyOrdersFilterState({
    isB2BUser,
    isCompanyOrder,
    selectedCompanyId,
    orderStatuses: getOrderStatuses,
  });
  const customerFilterState = useCustomerOrdersState({
    companyId: selectedCompanyId,
    orderStatuses: getOrderStatuses,
  });
  const companyFilterState = useCompanyOrdersState({
    selectedCompanyId,
    orderStatuses: getOrderStatuses,
  });

  const getActiveFilterState = () => {
    if (isUnifiedCompanyPath) return companyFilterState;
    if (isUnifiedCustomerPath) return customerFilterState;
    return legacyFilterState;
  };

  const {
    activeSort,
    handleSearchChange,
    handleFilterChange,
    handleCompanyIdsChange,
    handleSetOrderBy,
  } = getActiveFilterState();

  const getFilterDataAndOrderBy = () => {
    if (isUnifiedCompanyPath) {
      return adaptCompanyUnifiedToLegacyFilterParams({
        filters: companyFilterState.filters,
        activeSort: companyFilterState.activeSort,
      });
    }
    if (isUnifiedCustomerPath) {
      return adaptUnifiedToLegacyFilterParams({
        filters: customerFilterState.filters,
        activeSort: customerFilterState.activeSort,
        isB2BUser,
      });
    }
    return { filterData: legacyFilterState.filterData, orderBy: legacyFilterState.orderBy };
  };

  const { filterData, orderBy } = getFilterDataAndOrderBy();

  useEffect(() => {
    // TODO: Guest customer should not be able to see the order list
    if (role === CustomerRole.GUEST) return;

    const initFilter = async () => {
      const createdByUsers =
        isB2BUser && isCompanyOrder ? await getCreatedByUserForOrders(Number(companyId)) : {};
      //  Caution: This api hasn't yet been ported to unified order api
      const orderStatuses = isB2BUser ? await getOrderStatusType() : await getBcOrderStatusType();
      setOrderStatuses(orderStatuses);

      /* 
        returns what all fields to show in more filter (funnel icon)
        and styles associated to those fields
      */
      setFilterMoreInfo(
        translateFilterMoreData(
          getFilterMoreData(
            isB2BUser,
            role,
            isCompanyOrder,
            isAgenting,
            createdByUsers,
            orderStatuses,
          ),
          b3Lang,
        ),
      );
    };

    initFilter();
  }, [b3Lang, companyId, isAgenting, isB2BUser, isCompanyOrder, role]);

  const fetchUnifiedOrders = async (args: {
    first?: number;
    after?: string;
    last?: number;
    before?: string;
    filters: OrdersFiltersInput;
    sortBy: OrdersSortInput;
  }): Promise<{
    edges: ListItem[];
    totalCount: number;
    pageInfo: PageInfo | null;
  }> => {
    const result = await getCustomerOrders(args);
    const orders = result.data?.customer?.orders;
    const edges = (orders?.edges || []).map((edge) => mapSfGqlOrderToListItem(edge.node));
    const pageInfo = orders?.pageInfo ?? null;

    return { edges, totalCount: -1, pageInfo };
  };

  const fetchUnifiedCompanyOrders = async (args: {
    first?: number;
    after?: string;
    last?: number;
    before?: string;
    filters: CompanyOrdersFiltersInput;
    sortBy: OrdersSortInput;
  }): Promise<{
    edges: ListItem[];
    totalCount: number;
    pageInfo: PageInfo | null;
  }> => {
    const result = await getCompanyOrders(args);
    const orders = result.data?.customer?.activeCompany?.orders;
    const edges = (orders?.edges || []).map((edge) => mapSfGqlOrderToListItem(edge.node));
    const pageInfo = orders?.pageInfo ?? null;
    const totalCount = orders?.collectionInfo?.totalItems ?? -1;

    return { edges, totalCount, pageInfo };
  };

  const fetchLegacyOrders = async ({
    createdBy,
    ...params
  }: Partial<FilterSearchProps>): Promise<{ edges: ListItem[]; totalCount: number }> => {
    const { edges = [], totalCount } = isB2BUser
      ? await getB2BAllOrders({
          ...params,
          email: getEmail(createdBy),
          createdBy: getName(createdBy),
        })
      : await getBCAllOrders(params);

    setAllTotal(totalCount);

    return {
      edges: edges.map((row: PossibleNodeWrapper<object>) => {
        const order = ('node' in row ? row.node : row) as LegacyOrderListItem;

        return { ...order };
      }),
      totalCount,
    };
  };

  const navigate = useNavigate();

  const legacyGoToDetail = (item: ListItem, index: number) => {
    navigate(`/orderDetail/${item.orderId}`, {
      state: {
        currentIndex: index,
        searchParams: {
          ...filterData,
          orderBy,
        },
        totalCount: isUnifiedOrders ? -1 : allTotal,
        isCompanyOrder,
        beginDateAt: filterData?.beginDateAt,
        endDateAt: filterData?.endDateAt,
      },
    });
  };

  // TODO B2B-4629: Double check if need more params for unified path
  const goToDetail = (item: ListItem, index: number) => {
    navigate(`/orderDetail/${item.orderId}`, {
      state: {
        currentIndex: index,
        totalCount: -1,
        isCompanyOrder,
        unifiedCustomerFilters: customerFilterState.filters,
        unifiedCustomerSortBy: customerFilterState.sortBy,
      },
    });
  };

  const navigateToOrderDetail = isUnifiedCustomerPath ? goToDetail : legacyGoToDetail;

  const columnAllItems = [
    {
      key: 'orderId',
      title: b3Lang('orders.order'),
      width: '10%',
      isSortable: true,
      render: ({ orderId }) => orderId,
    },
    {
      key: 'internalOrderId',
      title: 'Internal Order ID',
      render: ({ internalOrderId }) => <Box>{internalOrderId || '–'}</Box>,
      width: '10%',
      isSortable: false,
    },
    {
      key: 'invoiceId',
      title: 'Invoice ID',
      render: ({ invoiceId }) => <Box>{invoiceId || '–'}</Box>,
      width: '10%',
      isSortable: false,
    },
    {
      key: 'companyName',
      title: b3Lang('orders.company'),
      width: '10%',
      isSortable: false,
      render: ({ companyInfo }) => {
        return <Box>{companyInfo?.companyName || '–'}</Box>;
      },
    },
    {
      key: 'poNumber',
      title: b3Lang('orders.poReference'),
      render: ({ poNumber }) => <Box>{poNumber || '–'}</Box>,
      width: '10%',
      isSortable: true,
    },
    {
      key: 'totalIncTax',
      title: b3Lang('orders.grandTotal'),
      render: ({ money, totalIncTax }) =>
        money
          ? ordersCurrencyFormat(JSON.parse(JSON.parse(money)), totalIncTax)
          : currencyFormat(totalIncTax),
      align: 'right',
      width: '8%',
      isSortable: true,
    },
    {
      key: 'status',
      title: b3Lang('orders.orderStatus'),
      render: ({ status, statusText }) => <OrderStatus text={statusText} code={status} />,
      width: '10%',
      isSortable: true,
    },
    {
      key: 'placedBy',
      title: b3Lang('orders.placedBy'),
      render: ({ firstName, lastName }) => `${firstName} ${lastName}`,
      width: '10%',
      isSortable: true,
    },
    {
      key: 'createdAt',
      title: b3Lang('orders.createdOn'),
      render: ({ createdAt }) => `${displayFormat(Number(createdAt))}`,
      width: '10%',
      isSortable: true,
    },
  ] as const satisfies TableColumnItem<ListItem>[];

  const getColumnItems = (): TableColumnItem<ListItem>[] => {
    const getNewColumnItems = columnAllItems.filter((item) => {
      const { key } = item;
      if (!isB2BUser && key === 'companyName') return false;
      if ((!isB2BUser || (Number(role) === 3 && !isAgenting)) && key === 'placedBy') return false;

      if (key === 'placedBy' && !(Number(role) === 3 && !isAgenting) && !isCompanyOrder) {
        return false;
      }
      return true;
    });

    return getNewColumnItems;
  };

  const columnItems = getColumnItems();

  const unifiedState = isUnifiedCompanyPath ? companyFilterState : customerFilterState;

  const getQueryKey = () => {
    if (isUnifiedCompanyPath) {
      return [
        'orderList:unifiedCompany',
        companyFilterState.filters,
        companyFilterState.sortBy,
        companyFilterState.paginationVariables,
      ];
    }
    if (isUnifiedCustomerPath) {
      return [
        'orderList:unified',
        customerFilterState.filters,
        customerFilterState.sortBy,
        customerFilterState.paginationVariables,
      ];
    }
    return ['orderList:legacy', filterData, legacyPagination, orderBy];
  };

  const getQueryFn = () => {
    if (isUnifiedCompanyPath) {
      return fetchUnifiedCompanyOrders({
        ...companyFilterState.paginationVariables,
        filters: companyFilterState.filters,
        sortBy: companyFilterState.sortBy,
      });
    }
    if (isUnifiedCustomerPath) {
      return fetchUnifiedOrders({
        ...customerFilterState.paginationVariables,
        filters: customerFilterState.filters,
        sortBy: customerFilterState.sortBy,
      });
    }
    return fetchLegacyOrders({ ...filterData, ...legacyPagination, orderBy });
  };

  const { data, isFetching, dataUpdatedAt } = useQuery({
    queryKey: getQueryKey(),
    enabled: isUnifiedOrders || Boolean(filterData),
    queryFn: getQueryFn,
  });

  useEffect(() => {
    if (!data || !isUnifiedOrders) return;
    const pageInfo = 'pageInfo' in data ? (data as { pageInfo: PageInfo | null }).pageInfo : null;
    if (pageInfo) unifiedState.updatePageInfo(pageInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, isUnifiedOrders]);

  useEffect(() => {
    if (!data?.edges?.length) return;

    const orderIds = data.edges
      .map((e) => Number(e.orderId))
      .filter((id) => !Number.isNaN(id) && id > 0);

    fetchOrderComments(orderIds)
      .then((map) => setOrderComments(map))
      .catch(() => setOrderComments({}));
  }, [data?.edges]);

  // Parallel MongoDB search: runs after BC settles and merges with BC results.
  // BC results are shown first; MongoDB fills in orders BC doesn't index
  // (internal order IDs, invoice IDs). Results are deduplicated by orderId.
  // Strategy:
  //   1. While BC is fetching, reset stale MongoDB state so we never merge with old data.
  //   2. Once BC settles, always query MongoDB for the same term — regardless of whether
  //      BC returned results. This prevents BC partial matches from hiding MongoDB results.
  //   3. Display merges: BC results first, then MongoDB-only (not already in BC list).
  //   4. Clearing the search resets MongoDB state.
  useEffect(() => {
    const q = filterData?.q;

    // No search term — reset MongoDB state.
    if (!q) {
      setMongoListItems(null);
      setMongoTotalCount(0);
      return;
    }

    // BC query in-flight — reset stale MongoDB results so we don't merge with old data.
    if (isFetching) {
      setMongoListItems(null);
      setMongoTotalCount(0);
      return;
    }

    // BC has settled — query MongoDB in parallel, regardless of BC result count.
    // Pass the logged-in BC customer ID so only that customer's orders are returned.
    const { offset, first } = legacyPagination;

    searchOrderComments(q, customerId || undefined, offset, first)
      .then(({ results, totalCount }) => {
        setMongoListItems(
          results.map((r) => ({
            orderId:      String(r.bcOrderId),
            internalOrderId: r.internalOrderId,
            invoiceId:    r.invoiceId,
            status:       r.status,
            statusText:   getOrderStatusText(r.status, getOrderStatuses),
            createdAt:    r.dateCreated
              ? String(Math.floor(new Date(r.dateCreated).getTime() / 1000))
              : '',
            companyName:  r.billingCompany,
            companyInfo:  r.billingCompany
              ? {
                  companyName:    r.billingCompany,
                  companyId:      '',
                  companyAddress: '',
                  companyCountry: '',
                  companyState:   '',
                  companyCity:    '',
                  companyZipCode: '',
                  phoneNumber:    '',
                  bcId:           '',
                }
              : undefined,
            poNumber:     r.poNumber,
            totalIncTax:  r.totalIncTax,
            firstName:    '',
            lastName:     '',
            money:        undefined,
          })),
        );
        setMongoTotalCount(totalCount);
      })
      .catch(() => {
        setMongoListItems([]);
        setMongoTotalCount(0);
      });
  // Re-run when: search term changes, BC loading state changes (trigger after BC settles),
  // pagination changes, or user changes.
  }, [filterData?.q, isFetching, legacyPagination, customerId, getOrderStatuses]);

  const listItems = useMemo(
    () =>
      (data?.edges ?? []).map((edge) => {
        const dbComment = orderComments[String(edge.orderId)];

        const mongoCompany = dbComment?.companyName;

        return {
          ...edge,
          statusText: getOrderStatusText(edge.status, getOrderStatuses),
          internalOrderId: dbComment?.internalOrderId || edge.internalOrderId || '',
          invoiceId: dbComment?.invoiceId || edge.invoiceId || '',
          // Use MongoDB billing_company when available — it has the mapped display name
          companyName: mongoCompany || edge.companyName,
          companyInfo: mongoCompany
            ? { ...edge.companyInfo, companyName: mongoCompany } as typeof edge.companyInfo
            : edge.companyInfo,
        };
      }),
    [data?.edges, getOrderStatuses, orderComments],
  );

  // Merge BC and MongoDB results: BC first, then MongoDB-only items not already in BC list
  // (deduplicated by orderId). Falls back to BC-only when no search term or MongoDB is pending.
  const mergedItems = useMemo(() => {
    if (!filterData?.q || mongoListItems === null) return listItems;

    const bcOrderIds = new Set(listItems.map((item) => String(item.orderId)));
    const mongoOnlyItems = mongoListItems.filter((item) => !bcOrderIds.has(String(item.orderId)));

    return [...listItems, ...mongoOnlyItems];
  }, [filterData?.q, listItems, mongoListItems]);

  // Keep the spinner running across the gap between BC returning 0 results and MongoDB
  // responding. Without this, the empty BC result causes a brief "No data" flash before
  // the MongoDB fallback populates the table.
  //
  // mongoListItems === null means "MongoDB has not been checked yet for this query".
  // mongoListItems === []   means "MongoDB was checked and also found nothing".
  const isAwaitingMongoFallback =
    Boolean(filterData?.q) && !isFetching && !data?.edges?.length && mongoListItems === null;

  return (
    <B3Spin isSpinning={isFetching || isAwaitingMongoFallback}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',

            '& > div': {
              width: isMobile ? '100%' : 'auto',
            },
          }}
        >
          {isEnabledCompanyHierarchy && (
            <Box sx={{ mr: isMobile ? 0 : '10px', mb: '30px' }}>
              <B2BAutoCompleteCheckbox handleChangeCompanyIds={handleCompanyIdsChange} />
            </Box>
          )}
          <B3Filter
            startPicker={{
              isEnabled: true,
              label: b3Lang('orders.from'),
              defaultValue: filterData?.beginDateAt || null,
              pickerKey: 'start',
            }}
            endPicker={{
              isEnabled: true,
              label: b3Lang('orders.to'),
              defaultValue: filterData?.endDateAt || null,
              pickerKey: 'end',
            }}
            filterMoreInfo={filterMoreInfo}
            handleChange={(key, value) => {
              handleSearchChange(key, value);
              if (key === 'search') setLegacyPagination({ offset: 0, first: 10 });
            }}
            handleFilterChange={handleFilterChange}
            pcTotalWidth="100%"
            pcContainerWidth="100%"
            pcSearchContainerWidth="100%"
          />
        </Box>

        <B3Table
          columnItems={columnItems}
          listItems={mergedItems}
          pagination={
            isUnifiedOrders
              ? unifiedState.b3TablePaginationProps.pagination
              : {
                  ...legacyPagination,
                  count: (data?.totalCount || 0) + mongoTotalCount,
                }
          }
          cursorPageInfo={
            isUnifiedOrders ? unifiedState.b3TablePaginationProps.cursorPageInfo : undefined
          }
          onPaginationChange={
            isUnifiedOrders
              ? unifiedState.b3TablePaginationProps.onPaginationChange
              : setLegacyPagination
          }
          isInfiniteScroll={isMobile}
          renderItem={(row, index) => (
            <OrderItemCard
              key={row.orderId}
              goToDetail={() => navigateToOrderDetail(row, index)}
              item={row}
            />
          )}
          onClickRow={navigateToOrderDetail}
          sortDirection={activeSort.dir}
          sortByFn={handleSetOrderBy}
          orderBy={activeSort.key}
        />
      </Box>
    </B3Spin>
  );
}

export default Order;
